// Client Memberships View Component
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Search,
  Refresh,
  CardMembership,
  Person,
  CalendarToday,
  CheckCircle,
  Cancel,
  Block,
} from "@mui/icons-material";
import apiService from "../../services/api";
import { useCurrency } from "../../contexts/CurrencyContext";
import { format } from "date-fns";

const ClientMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useCurrency(); // keep for potential future currency formatting

  useEffect(() => {
    fetchData();
    // `fetchData` is defined in-component and is stable for our usage here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsRes] = await Promise.all([
        apiService.get("/clients"),
      ]);

      if (clientsRes.success) {
        setClients(clientsRes.data.clients || []);
        // Fetch memberships for all clients
        await fetchAllMemberships(clientsRes.data.clients || []);
      }
    } catch (err) {
      setError("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMemberships = async (clientsList) => {
    try {
      const membershipPromises = clientsList.map(async (client) => {
        try {
          const response = await apiService.get(`/memberships/client/${client.id}`);
          if (response.success) {
            const list = Array.isArray(response.data)
              ? response.data
              : (response.data?.memberships || []);
            if (!Array.isArray(list) || list.length === 0) return [];
            return list.map((membership) => ({
              ...membership,
              client_name: `${client.first_name} ${client.last_name}`.trim(),
              client_mobile: client.mobile,
              client_email: client.email,
            }));
          }
          return [];
        } catch (err) {
          console.error(`Error fetching memberships for client ${client.id}:`, err);
          return [];
        }
      });

      const allMemberships = await Promise.all(membershipPromises);
      const flattened = allMemberships.flat();
      setMemberships(flattened);
    } catch (err) {
      console.error("Error fetching all memberships:", err);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleClientFilterChange = (event) => {
    setClientFilter(event.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expired":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle fontSize="small" />;
      case "expired":
        return <Cancel fontSize="small" />;
      case "cancelled":
        return <Block fontSize="small" />;
      default:
        return null;
    }
  };

  const isActive = (membership) => {
    if (membership.status !== "active") return false;
    const today = new Date();
    const startDate = new Date(membership.start_date);
    // Lifetime memberships have null end_date
    if (!membership.end_date) {
      return today >= startDate;
    }
    const endDate = new Date(membership.end_date);
    return today >= startDate && today <= endDate;
  };

  const filteredMemberships = memberships.filter((membership) => {
    const matchesSearch =
      membership.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      membership.membership_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      membership.client_mobile?.includes(searchTerm);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && isActive(membership)) ||
      (statusFilter === "expired" && membership.status === "expired") ||
      (statusFilter === "cancelled" && membership.status === "cancelled") ||
      (statusFilter === "inactive" && !isActive(membership) && membership.status === "active");

    const matchesClient = clientFilter === "all" || String(membership.client_id) === String(clientFilter);

    return matchesSearch && matchesStatus && matchesClient;
  });

  const handleViewDetails = (membership) => {
    setSelectedMembership(membership);
    setViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setViewDialogOpen(false);
    setSelectedMembership(null);
  };

  // Calculate statistics
  const stats = {
    total: memberships.length,
    active: memberships.filter((m) => isActive(m)).length,
    expired: memberships.filter((m) => m.status === "expired").length,
    cancelled: memberships.filter((m) => m.status === "cancelled").length,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Client Memberships
        </Typography> 
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Memberships
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Expired
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.expired}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cancelled
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.cancelled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by client name, membership, or mobile..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="inactive">Inactive (Past Due)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Client</InputLabel>
            <Select value={clientFilter} label="Client" onChange={handleClientFilterChange}>
              <MenuItem value="all">All Clients</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.first_name} {client.last_name} ({client.mobile})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={fetchData} title="Refresh">
            <Refresh />
          </IconButton>
        </Box>
      </Paper>

      {/* Memberships Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Membership Plan</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Discount %</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredMemberships.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No memberships found
                </TableCell>
              </TableRow>
            ) : (
              filteredMemberships.map((membership) => {
                const startDate = new Date(membership.start_date);
                const endDate = membership.end_date ? new Date(membership.end_date) : null;
                const today = new Date();
                const daysRemaining = endDate ? Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)) : null;
                const isCurrentlyActive = isActive(membership);

                return (
                  <TableRow
                    key={membership.id}
                    sx={{
                      backgroundColor: isCurrentlyActive ? "rgba(76, 175, 80, 0.05)" : "inherit",
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Person fontSize="small" color="action" />
                        {membership.client_name || "N/A"}
                      </Box>
                    </TableCell>
                    <TableCell>{membership.client_mobile || "N/A"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CardMembership fontSize="small" color="primary" />
                        {membership.membership_name || "N/A"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        {format(startDate, "dd MMM yyyy")}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        {endDate ? format(endDate, "dd MMM yyyy") : "Lifetime"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {membership.duration_type === 'lifetime' || !membership.duration_months 
                        ? "Lifetime" 
                        : `${membership.duration_months} ${membership.duration_months === 1 ? "month" : "months"}`}
                      {isCurrentlyActive && daysRemaining !== null && daysRemaining > 0 && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {daysRemaining} days left
                        </Typography>
                      )}
                      {isCurrentlyActive && daysRemaining === null && (
                        <Typography variant="caption" display="block" color="success.main">
                          Never expires
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{membership.discount_percentage}%</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(membership.status)}
                        label={isCurrentlyActive ? "Active" : membership.status}
                        color={getStatusColor(isCurrentlyActive ? "active" : membership.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(membership)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Membership Details</DialogTitle>
        <DialogContent>
          {selectedMembership && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Client Name
                </Typography>
                <Typography variant="body1">{selectedMembership.client_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Mobile
                </Typography>
                <Typography variant="body1">{selectedMembership.client_mobile}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Membership Plan
                </Typography>
                <Typography variant="body1">{selectedMembership.membership_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Duration Type
                </Typography>
                <Typography variant="body1">{selectedMembership.duration_type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {format(new Date(selectedMembership.start_date), "dd MMM yyyy")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  End Date
                </Typography>
                <Typography variant="body1">
                  {selectedMembership.end_date 
                    ? format(new Date(selectedMembership.end_date), "dd MMM yyyy")
                    : "Lifetime (Never expires)"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedMembership.status)}
                  label={isActive(selectedMembership) ? "Active" : selectedMembership.status}
                  color={getStatusColor(isActive(selectedMembership) ? "active" : selectedMembership.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Discount Percentage
                </Typography>
                <Typography variant="body1">{selectedMembership.discount_percentage}%</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Auto Renew
                </Typography>
                <Typography variant="body1">
                  {selectedMembership.auto_renew ? "Yes" : "No"}
                </Typography>
              </Grid>
              {selectedMembership.next_renewal_date && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Next Renewal Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedMembership.next_renewal_date), "dd MMM yyyy")}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {format(new Date(selectedMembership.created_at), "dd MMM yyyy HH:mm")}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientMemberships;

